import React, { useState, useEffect } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Alert,
    LinearProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    ContentCopy as CopyIcon,
    TrendingUp as TrendingUpIcon,
    Security as SecurityIcon,
    Speed as SpeedIcon,
    Analytics as AnalyticsIcon
} from '@mui/icons-material';
// Chart.js imports - installed via yarn add react-chartjs-2 chart.js
import { Line, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import MainCard from 'ui-component/cards/MainCard';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { fetchApiKeys, createApiKey, deleteApiKey, fetchGatewayMetrics, fetchTenants } from 'store/slices/apiGateway';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

const ApiGatewayDashboard = () => {
    const theme = useTheme();
    const dispatch = useDispatch();

    // Redux state
    const { apiKeys, gatewayMetrics: metrics, tenants, selectedTenant, loading, error } = useSelector((state) => state.apiGateway);

    // Local state
    const [createKeyDialog, setCreateKeyDialog] = useState(false);
    const [keyToDelete, setKeyToDelete] = useState(null);
    const [newKey, setNewKey] = useState({
        name: '',
        description: '',
        scopes: ['read:data'],
        rate_limit_tier: 'standard',
        expires_in_days: 30
    });
    const [generatedKey, setGeneratedKey] = useState(null);
    const [showKey, setShowKey] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            await dispatch(fetchApiKeys());
            await dispatch(fetchGatewayMetrics());
            await dispatch(fetchTenants());
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    const handleCreateKey = async () => {
        try {
            const result = await dispatch(createApiKey(newKey));

            setGeneratedKey(result);
            setCreateKeyDialog(false);
            setNewKey({
                name: '',
                description: '',
                scopes: ['read:data'],
                rate_limit_tier: 'standard',
                expires_in_days: 30
            });
        } catch (error) {
            console.error('Error creating API key:', error);
        }
    };

    const handleDeleteKey = async (keyId) => {
        try {
            await dispatch(deleteApiKey(keyId));
            setKeyToDelete(null);
        } catch (error) {
            console.error('Error deleting API key:', error);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const getRateLimitColor = (tier) => {
        switch (tier) {
            case 'free':
                return 'default';
            case 'standard':
                return 'primary';
            case 'premium':
                return 'secondary';
            case 'enterprise':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusColor = (isActive) => {
        return isActive ? 'success' : 'default';
    };

    // Chart configurations
    const requestsChartData = {
        labels: metrics?.requests_by_hour?.map((item) => item.hour) || [],
        datasets: [
            {
                label: 'Requests per Hour',
                data: metrics?.requests_by_hour?.map((item) => item.requests) || [],
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
                tension: 0.4
            }
        ]
    };

    const endpointsChartData = {
        labels: metrics?.top_endpoints?.map((item) => item.endpoint) || [],
        datasets: [
            {
                label: 'Requests',
                data: metrics?.top_endpoints?.map((item) => item.requests) || [],
                backgroundColor: [
                    theme.palette.primary.main,
                    theme.palette.secondary.main,
                    theme.palette.warning.main,
                    theme.palette.info.main,
                    theme.palette.success.main
                ]
            }
        ]
    };

    const statusCodesChartData = {
        labels: Object.keys(metrics?.status_codes || {}),
        datasets: [
            {
                data: Object.values(metrics?.status_codes || {}),
                backgroundColor: [
                    theme.palette.success.main,
                    theme.palette.success.light,
                    theme.palette.warning.main,
                    theme.palette.error.main,
                    theme.palette.error.main,
                    theme.palette.error.light,
                    theme.palette.error.dark
                ]
            }
        ]
    };

    if (loading) {
        return (
            <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                API Gateway Dashboard
            </Typography>

            {/* Metrics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Requests (24h)
                                    </Typography>
                                    <Typography variant="h4">{metrics?.total_requests_24h?.toLocaleString()}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <SpeedIcon color="secondary" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Avg Response Time
                                    </Typography>
                                    <Typography variant="h4">{metrics?.average_response_time}ms</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <SecurityIcon color="success" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Success Rate
                                    </Typography>
                                    <Typography variant="h4">
                                        {((metrics?.successful_requests_24h / metrics?.total_requests_24h) * 100).toFixed(1)}%
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <AnalyticsIcon color="warning" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Active API Keys
                                    </Typography>
                                    <Typography variant="h4">{metrics?.active_api_keys}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <MainCard title="Request Volume (24h)">
                        <Box sx={{ height: 300 }}>
                            <Line data={requestsChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                        </Box>
                    </MainCard>
                </Grid>

                <Grid item xs={12} md={4}>
                    <MainCard title="Status Codes">
                        <Box sx={{ height: 300 }}>
                            <Doughnut data={statusCodesChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                        </Box>
                    </MainCard>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    {/* API Keys Table */}
                    <MainCard
                        title="API Keys"
                        secondary={
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateKeyDialog(true)}>
                                Create API Key
                            </Button>
                        }
                    >
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Key Prefix</TableCell>
                                        <TableCell>Scopes</TableCell>
                                        <TableCell>Rate Limit</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Last Used</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {apiKeys.map((key) => (
                                        <TableRow key={key.id}>
                                            <TableCell>{key.name}</TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center">
                                                    <Typography variant="body2" fontFamily="monospace">
                                                        {key.key_prefix}...
                                                    </Typography>
                                                    <IconButton size="small" onClick={() => copyToClipboard(key.key_prefix)}>
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    {key.scopes.map((scope) => (
                                                        <Chip key={scope} label={scope} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={key.rate_limit_tier}
                                                    color={getRateLimitColor(key.rate_limit_tier)}
                                                    size="small"
                                                />
                                                <Typography variant="caption" display="block">
                                                    {key.requests_per_hour}/h
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={key.is_active ? 'Active' : 'Inactive'}
                                                    color={getStatusColor(key.is_active)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" color="primary">
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => setKeyToDelete(key.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </MainCard>
                </Grid>

                <Grid item xs={12} md={4}>
                    <MainCard title="Top Endpoints">
                        <Box>
                            {metrics?.top_endpoints?.map((endpoint, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" fontFamily="monospace">
                                            {endpoint.endpoint}
                                        </Typography>
                                        <Typography variant="body2" color="primary">
                                            {endpoint.requests.toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(endpoint.requests / metrics.top_endpoints[0].requests) * 100}
                                        sx={{ mt: 0.5 }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </MainCard>
                </Grid>
            </Grid>

            {/* Create API Key Dialog */}
            <Dialog open={createKeyDialog} onClose={() => setCreateKeyDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New API Key</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Key Name"
                        fullWidth
                        variant="outlined"
                        value={newKey.name}
                        onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Description"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={3}
                        value={newKey.description}
                        onChange={(e) => setNewKey({ ...newKey, description: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Rate Limit Tier</InputLabel>
                        <Select value={newKey.rate_limit_tier} onChange={(e) => setNewKey({ ...newKey, rate_limit_tier: e.target.value })}>
                            <MenuItem value="free">Free (100/h)</MenuItem>
                            <MenuItem value="standard">Standard (1,000/h)</MenuItem>
                            <MenuItem value="premium">Premium (10,000/h)</MenuItem>
                            <MenuItem value="enterprise">Enterprise (100,000/h)</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="Expires in Days"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={newKey.expires_in_days}
                        onChange={(e) => setNewKey({ ...newKey, expires_in_days: parseInt(e.target.value) })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateKeyDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateKey} variant="contained">
                        Create Key
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Generated Key Dialog */}
            <Dialog open={!!generatedKey} onClose={() => setGeneratedKey(null)} maxWidth="sm" fullWidth>
                <DialogTitle>API Key Created Successfully</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Store this key securely. It will not be shown again.
                    </Alert>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            API Key:
                        </Typography>
                        <Box display="flex" alignItems="center">
                            <TextField
                                value={generatedKey?.secret_key || ''}
                                type={showKey ? 'text' : 'password'}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <Box>
                                            <IconButton onClick={() => setShowKey(!showKey)}>
                                                {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                            <IconButton onClick={() => copyToClipboard(generatedKey?.secret_key || '')}>
                                                <CopyIcon />
                                            </IconButton>
                                        </Box>
                                    )
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGeneratedKey(null)} variant="contained">
                        I've Saved the Key
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!keyToDelete} onClose={() => setKeyToDelete(null)}>
                <DialogTitle>Delete API Key</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this API key? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setKeyToDelete(null)}>Cancel</Button>
                    <Button onClick={() => handleDeleteKey(keyToDelete)} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ApiGatewayDashboard;
